/**
 * [INPUT]: 依赖 react，依赖 ../scorer 的 DiagnoseResponse / DiagnoseStrengthItem / DiagnoseAdjustmentItem
 * [OUTPUT]: 对外提供 AiReportView 组件（style_tag + overall_analysis + 强弱项 + 7 天行动计划）
 * [POS]: apps/web/components 的 AI 诊断报告呈现层；对齐 docs/ai-diagnosis-architecture §2 终态、§4.2 输出 schema
 * [PROTOCOL]: 变更时更新此头部，然后检查 ../../CLAUDE.md
 *
 * 暗色卡片风格对齐 ResultView；挂载淡入（浏览器 useEffect 触发，SSR 仍含全部文案）。
 */
import { useEffect, useState } from 'react'
import type { DiagnoseResponse } from '../scorer'

interface Props {
  report: DiagnoseResponse
  onRetry?: () => void
}

const PLAN_STAGES: Array<{ key: keyof DiagnoseResponse['action_plan_7days']; label: string }> = [
  { key: 'day1_3', label: 'Days 1–3' },
  { key: 'day4_5', label: 'Days 4–5' },
  { key: 'day6_7', label: 'Days 6–7' },
]

export function AiReportView({ report, onRetry }: Props) {
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  return (
    <div
      className="max-w-md w-full space-y-5"
      style={{ opacity: mounted ? 1 : 0, transition: 'opacity 400ms ease-out' }}
    >
      {/* 标题 + 风格标签 */}
      <div className="rounded-2xl border border-brass/30 bg-gradient-to-br from-brass/10 to-amber/5 p-6">
        <div className="flex items-center gap-2 text-xs text-brass mb-3 uppercase tracking-wider font-mono">
          <span className="inline-block w-2 h-2 rounded-full bg-brass animate-pulse" />
          AI Expert Diagnosis
        </div>
        <div className="text-2xl font-bold text-white">{report.style_tag}</div>
        <p className="text-sm text-slate-300 leading-relaxed mt-3">{report.overall_analysis}</p>
      </div>

      {/* 强弱项 */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-brass/15 hover:border-brass/25 transition-colors duration-300 bg-slate-900/60 p-4">
          <div className="text-xs text-olive font-semibold mb-2">Strengths</div>
          <ul className="space-y-2 text-xs text-slate-300">
            {report.strengths_analysis.map((s, i) => (
              <li key={i}>
                <div className="text-slate-200 font-medium">{s.feature}</div>
                <div className="text-slate-400 mt-0.5">{s.description}</div>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-brass/15 hover:border-brass/25 transition-colors duration-300 bg-slate-900/60 p-4">
          <div className="text-xs text-terracotta font-semibold mb-2">Adjustable</div>
          <ul className="space-y-2 text-xs text-slate-300">
            {report.adjustments_analysis.map((s, i) => (
              <li key={i}>
                <div className="text-slate-200 font-medium">{s.feature}</div>
                <div className="text-slate-400 mt-0.5">{s.description}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* 7 天行动计划 */}
      <div className="rounded-2xl border border-brass/15 hover:border-brass/25 transition-colors duration-300 bg-slate-900/60 p-5">
        <div className="text-sm font-semibold text-slate-200 mb-3">7-Day Action Plan (Natural Methods)</div>
        <div className="space-y-3">
          {PLAN_STAGES.map((stage) => (
            <div key={stage.key} className="flex flex-col sm:flex-row sm:gap-4 gap-1">
              <div className="shrink-0 w-20 text-xs text-brass font-semibold pt-0.5">{stage.label}</div>
              <div className="text-xs text-slate-300 leading-relaxed flex-1">
                {report.action_plan_7days[stage.key]}
              </div>
            </div>
          ))}
        </div>
      </div>

      {onRetry && (
        <button
          onClick={onRetry}
          className="block mx-auto text-xs text-slate-500 hover:text-slate-300"
        >
          Re-diagnose
        </button>
      )}
    </div>
  )
}